import { trpc as AppDeliverConfig } from '@tencent/tpro_rmonitor_manage_server_app_deliver_config';
import { trpc as ComplianceConfig } from '@tencent/tpro_rmonitor_manage_server_compliance_config';
import { get } from 'lodash-es';
import moment from 'moment';
import * as uuid from 'uuid';
import { getUserId } from '@/infrastructure/auth';
import { TRPCPost } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { logger } from '@/infrastructure/logger';
import { getProductId, isFlutter, getPid } from '@/infrastructure/product-info';
import { Platform } from '@/infrastructure/utils/app';
import { MatchMode } from '@/presentation/components/form/standards/types';
import {
  APP_VERSION_MODE_OPTIONS,
} from '@/presentation/pages/setting/app-deliver-config/config-modal/edit';
import { filterConfigHttp } from '@/presentation/pages/setting/app-deliver-config/utils';
import { WithTypeCast } from '@raft/utils';

// 功能配置列表
export type IConfigListReq = WithTypeCast<AppDeliverConfig.rmonitor.manage_server.IConfigListReq
& ComplianceConfig.rmonitor.manage_server.IComplianceConfigListReq>;
export type IConfigListResp = WithTypeCast<AppDeliverConfig.rmonitor.manage_server.IConfigListResp
| ComplianceConfig.rmonitor.manage_server.IComplianceConfigListResp>;
// 新增、更新配置
export type IConfig = WithTypeCast<AppDeliverConfig.rmonitor.manage_server.IConfig
| ComplianceConfig.rmonitor.manage_server.IComplianceConfig>;
export type IConfigOperateResp = WithTypeCast<AppDeliverConfig.rmonitor.manage_server.IConfigOperateResp
| ComplianceConfig.rmonitor.manage_server.IComplianceConfigOperateResp>;
// 查看配置
export type IConfigIDReq = WithTypeCast<AppDeliverConfig.rmonitor.manage_server.IConfigIDReq
| ComplianceConfig.rmonitor.manage_server.IComplianceConfigIDReq>;
export type IConfigInfoResp = WithTypeCast<AppDeliverConfig.rmonitor.manage_server.IConfigInfoResp
| ComplianceConfig.rmonitor.manage_server.IComplianceConfigInfoResp>;
// 默认配置
export type IDefaultConfigReq = WithTypeCast<AppDeliverConfig.rmonitor.manage_server.IDefaultConfigReq
| ComplianceConfig.rmonitor.manage_server.IDefaultComplianceConfigReq>;
export type IDefaultConfigResp = WithTypeCast<AppDeliverConfig.rmonitor.manage_server.IDefaultConfigResp
| ComplianceConfig.rmonitor.manage_server.IDefaultComplianceConfigResp>;
// 查看修改历史
export type IShowHistoryResp = WithTypeCast<AppDeliverConfig.rmonitor.manage_server.IShowHistoryResp>;

export enum ConfigTabType {
  BUSINESS,
  DEFAULT,
  PLATFORM,
  GLOBAL,
  COMPLIANCE,
}

enum ApiOperator {
  SELECT_DETAIL,
  SHOW_LIST,
  DELETE,
  UPDATE,
  INSERT,
  SET_ENABLE,
  GET_DEFAULT,
}


export interface Config {
  productId: string;
  name: string;
  appVersion?: string;
  appVersionV5?: string;
  sdkVersion?: string;
  system?: string;
  model?: string;
  modelV5?: string;
  uin?: string;
  deviceId?: string;
  priority: number;
  content?: string;
  createUser: string;
  createTime: number;
  updateUser: string;
  updateTime: number;
  enabled: 0 | 1;
  memo?: string;
  appVersionMode?: string;
  platform?: Platform;
}

export interface FormatConfig {
  id: number;
  productId: string | string[];
  name: string;
  appVersion?: string[];
  sdkVersion?: string[];
  system?: string[];
  model?: string[];
  uin?: string[];
  deviceId?: string[];
  priority: number;
  content?: string;
  createUser: string;
  createTime: number;
  updateUser: string;
  updateTime: number;
  enabled: 0 | 1;
  memo?: string;
  appVersionMode?: string[];
  platform?: number;
  osVersionOperator?: string;
  appVersionOperator?: string;
  sdkVersionOperator?: string;
}

export interface ConfigListReq {
  name?: string;
  page: number;
  pageSize: number;
}

export interface FullConfig extends Config {
  id: number;
  auth?: boolean;
}

export interface ConfigItem extends FullConfig {
  index?: number;
}

export interface ListData {
  config: ConfigItem[];
  total: string;
}

export const isConfigManage = (type: ConfigTabType) => type === ConfigTabType.PLATFORM
  || type === ConfigTabType.DEFAULT;

const getUrl = (type: ConfigTabType, operator: ApiOperator) => {
  const urlMap = {
    [ConfigTabType.DEFAULT]: {
      [ApiOperator.INSERT]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/InsertInnerConfig`,
      [ApiOperator.UPDATE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/UpdateInnerConfig`,
      [ApiOperator.DELETE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/DelInnerConfig`,
      [ApiOperator.SELECT_DETAIL]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/SelectInnerConfigDetail`,
      [ApiOperator.SHOW_LIST]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/ShowInnerConfigList`,
      [ApiOperator.SET_ENABLE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/SetInnerEnable`,
    },
    [ConfigTabType.PLATFORM]: {
      [ApiOperator.INSERT]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/InsertInnerConfig`,
      [ApiOperator.UPDATE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/UpdateInnerConfig`,
      [ApiOperator.DELETE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/DelInnerConfig`,
      [ApiOperator.SELECT_DETAIL]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/SelectInnerConfigDetail`,
      [ApiOperator.SHOW_LIST]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/ShowInnerConfigList`,
      [ApiOperator.SET_ENABLE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/SetInnerEnable`,
    },
    [ConfigTabType.COMPLIANCE]: {
      [ApiOperator.INSERT]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppComplianceConfig/InsertComplianceConfig`,
      [ApiOperator.UPDATE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppComplianceConfig/UpdateComplianceConfig`,
      [ApiOperator.DELETE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppComplianceConfig/DelComplianceConfig`,
      [ApiOperator.SELECT_DETAIL]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppComplianceConfig/SelectComplianceConfigDetail`,
      [ApiOperator.SHOW_LIST]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppComplianceConfig/ShowComplianceConfigList`,
      [ApiOperator.SET_ENABLE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppComplianceConfig/SetComplianceEnable`,
    },
    [ConfigTabType.BUSINESS]: {
      [ApiOperator.INSERT]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/InsertConfig`,
      [ApiOperator.UPDATE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/UpdateConfig`,
      [ApiOperator.DELETE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/DelConfig`,
      [ApiOperator.SELECT_DETAIL]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/SelectConfigDetail`,
      [ApiOperator.SHOW_LIST]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/ShowConfigList`,
      [ApiOperator.SET_ENABLE]: `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/SetEnable`,
    },
  };
  return get(urlMap, `${type}.${operator}`, '');
};

// 配置列表
export const getConfigList = async (
  type: ConfigTabType,
  params: IConfigListReq,
): Promise<ListData> => {
  const { page, pageSize } = params;
  const reqBody = {
    ...params,
    productId: isConfigManage(type) ? undefined : getProductId(),
    type: type !== ConfigTabType.COMPLIANCE ? type : undefined,
    platform: isConfigManage(type) ? params.platform : undefined,
  };
  const url = getUrl(type, ApiOperator.SHOW_LIST);
  try {
    const data = await TRPCPost<IConfigListResp>(url, reqBody);
    const listData = data.config;
    const addIndex = (data: any, page: number, pageSize: number) => {
      const baseNum = (page - 1) * pageSize + 1;
      return data.map((item: any, index: number) => ({
        ...item,
        content: type !== ConfigTabType.COMPLIANCE && params.platform === Platform.Android
          ? filterConfigHttp(item?.content, type) : item?.content,
        index: baseNum + index,
        appVersion: JSON.parse(item.appVersion ?? '[]'),
        model: JSON.parse(item.model ?? '[]'),
        sdkVersion: JSON.parse(item.sdkVersion ?? '[]'),
        system: JSON.parse(item.system ?? '[]'),
        uin: JSON.parse(item.uin ?? '[]'),
        deviceId: JSON.parse(item.deviceId ?? '[]'),
        appVersionMode: JSON.parse(item.appVersionMode ?? '[]'),
      }));
    };

    return {
      config: addIndex(listData, page!, pageSize!),
      total: data.totalCount,
    };
  } catch (e) {
    logger.error('getConfigList', e);
    return {
      config: [],
      total: '0',
    };
  }
};

export interface ModalFormData {
  name: string;
  appVersion?: string;
  sdkVersion?: string;
  system?: string;
  model?: string;
  uin?: string;
  deviceId?: string;
  priority: number;
  content: string;
  memo: string;
  appVersionMode: string[];
  productId?: string;
  platform?: number;
  appVersionOperator: string,
  sdkVersionOperator: string,
  osVersionOperator: string,
}

// 新增配置
export const insertConfig = async (
  type: ConfigTabType,
  data: ModalFormData,
): Promise<IConfigOperateResp> => {
  const platform = isFlutter() ? Platform.Flutter : Number(getPid());
  const params: IConfig = {
    productId: isConfigManage(type) ? undefined : getProductId(),
    name: data.name,
    appVersion: JSON.stringify(data.appVersion ?? []),
    sdkVersion: JSON.stringify(data.sdkVersion ?? []),
    system: JSON.stringify(data.system ?? []),
    model: JSON.stringify(data.model ?? []),
    uin: JSON.stringify(data.uin ?? []),
    deviceId: JSON.stringify(data.deviceId ?? []),
    priority: data.priority,
    content: data.content,
    createUser: getUserId(),
    createTime: moment().unix(),
    updateUser: getUserId(),
    updateTime: moment().unix(),
    enabled: 0,
    memo: data.memo,
    appVersionMode: JSON.stringify(formatAppVersionMode(data.appVersionMode || []) ?? []),
    type: type !== ConfigTabType.COMPLIANCE ? type : undefined,
    platform: isConfigManage(type) ? data.platform : platform,
    // 新建默认配置和平台配置专用
    productIds: isConfigManage(type) ? JSON.stringify(data.productId ?? []) : undefined,
    appVersionOperator: data.appVersionOperator ?? '',
    sdkVersionOperator: data.sdkVersionOperator ?? '',
    osVersionOperator: data.osVersionOperator ?? '',
  };
  const url = getUrl(type, ApiOperator.INSERT);
  try {
    return await TRPCPost<IConfigOperateResp>(url, params);
  } catch (e) {
    logger.error('insertConfig e:', e);
    return {};
  }
};

// 更新配置
export const updateConfig = async (
  type: ConfigTabType,
  data: ModalFormData,
  createUser: string,
  createTime: number,
  enabled: 0 | 1,
  id: number,
): Promise<IConfigOperateResp> => {
  const platform = isFlutter() ? Platform.Flutter : Number(getPid());
  const params: IConfig = {
    id,
    productId: isConfigManage(type) ? undefined : getProductId(),
    name: data.name,
    appVersion: JSON.stringify(data.appVersion ?? []),
    sdkVersion: JSON.stringify(data.sdkVersion ?? []),
    system: JSON.stringify(data.system ?? []),
    model: JSON.stringify(data.model ?? []),
    uin: JSON.stringify(data.uin ?? []),
    deviceId: JSON.stringify(data.deviceId ?? []),
    priority: data.priority,
    content: data.content,
    createUser,
    createTime,
    appVersionOperator: data.appVersionOperator ?? '',
    sdkVersionOperator: data.sdkVersionOperator ?? '',
    osVersionOperator: data.osVersionOperator ?? '',
    updateUser: getUserId(),
    updateTime: moment().unix(),
    enabled,
    memo: data.memo,
    appVersionMode: JSON.stringify(formatAppVersionMode(data.appVersionMode || []) ?? []),
    type: type !== ConfigTabType.COMPLIANCE ? type : undefined,
    platform: isConfigManage(type) ? data.platform : platform,
    // 更新默认配置和平台配置专用
    productIds: isConfigManage(type) ? JSON.stringify(data.productId ?? []) : undefined,
  };
  const url = getUrl(type, ApiOperator.UPDATE);
  try {
    return await TRPCPost<IConfigOperateResp>(url, params);
  } catch (e) {
    logger.error('updateConfig e:', e);
    return {};
  }
};

// 删除配置
export const deleteConfig = async (
  type: ConfigTabType,
  id: number,
): Promise<IConfigOperateResp> => {
  try {
    const params: IConfig = {
      id,
      productId: isConfigManage(type) ? undefined : getProductId(),
      updateUser: getUserId(),
      type: type !== ConfigTabType.COMPLIANCE ? type : undefined,
    };
    const url = getUrl(type, ApiOperator.DELETE);
    return await TRPCPost<IConfigOperateResp>(url, params);
  } catch (e) {
    logger.error('deleteConfig e:', e);
    return {};
  }
};

// 查看配置详情
export const loadConfigDetail = async (
  type: ConfigTabType,
  id: number,
) => {
  const url = getUrl(type, ApiOperator.SELECT_DETAIL);
  try {
    const reqBody = {
      type: type !== ConfigTabType.COMPLIANCE ? type : undefined,
      id,
    };
    const { config = {} } = await TRPCPost(url, reqBody);
    if (type === ConfigTabType.BUSINESS && (config.name === 'V5配置' || config.name === 'V6配置')) {
      config.appVersion = config.appVersionV5 === '' ? [] : [config.appVersionV5];
      config.model = config.modelV5 === '' ? [] : [config.modelV5];
    } else {
      config.appVersion = JSON.parse(config.appVersion ?? '[]');
      config.model = JSON.parse(config.model ?? '[]');
    }
    if (isConfigManage(type)) {
      config.productId = JSON.parse(config.productId ?? '[]');
    }
    config.sdkVersion = JSON.parse(config.sdkVersion ?? '[]');
    config.system = JSON.parse(config.system ?? '[]');
    config.uin = JSON.parse(config.uin ?? '[]');
    config.deviceId = JSON.parse(config.deviceId ?? '[]');
    config.appVersionMode = parseAppVersionMode(config.appVersionMode ?? '[]');
    if (type !== ConfigTabType.COMPLIANCE && config?.platform === Platform.Android) {
      config.content = filterConfigHttp(config?.content, type);
    }
    return config;
  } catch (error) {
    logger.debug('selectConfigDetail', error);
    return undefined;
  }
};


// 开关配置
export const setEnable = async (
  type: ConfigTabType,
  id: number,
  enabled: 0 | 1,
  updateUser: string,
): Promise<IConfigOperateResp> => {
  const params: IConfig = {
    id,
    enabled,
    productId: isConfigManage(type) ? undefined : getProductId(),
    updateUser,
    type: type !== ConfigTabType.COMPLIANCE ? type : undefined,
  };
  const url = getUrl(type, ApiOperator.SET_ENABLE);
  try {
    return await TRPCPost<IConfigOperateResp>(url, params);
  } catch (e) {
    logger.error('setEnable e:', e);
    return {};
  }
};

// 默认配置
export const getDefaultConfig = async (type: ConfigTabType, addPlatform?: Platform) => {
  const platform = isFlutter() ? Platform.Flutter : Number(getPid());
  const reqBody = {
    platform: isConfigManage(type) ? addPlatform : platform,
    type: type !== ConfigTabType.COMPLIANCE ? type : undefined,
  };
  const url = type === ConfigTabType.COMPLIANCE
    ? `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppComplianceConfig/GetComplianceDefaultConfig`
    : `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/GetDefaultConfig`;
  try {
    const data = await TRPCPost(url, reqBody);
    // 奇怪的逻辑，后端无法处理，需要前端过滤为Android时的http配置项
    if (addPlatform === Platform.Android && type !== ConfigTabType.COMPLIANCE) {
      return filterConfigHttp(data?.defalutContent ?? data?.defalut_content, type);
    }
    return data?.defalutContent ?? data?.defalut_content;
  } catch (e) {
    return undefined;
  }
};

// 查看修改历史
export const showHistory = async (type: ConfigTabType) => {
  try {
    const reqBody = {
      productId: isConfigManage(type) ? undefined : getProductId(),
      type: type !== ConfigTabType.COMPLIANCE ? type : undefined,
    };
    const { history = [] } = await TRPCPost<IShowHistoryResp>(
      `/${API_PREFIX}/trpc/trpc.rmonitor.manage_server.AppDeliverConfig/ShowHistory`,
      reqBody,
    );
    const formatHistory = (data: string) => {
      const config = JSON.parse(data);
      if (isConfigManage(type)) {
        try {
          config.productId = JSON.parse(config.productId);
        } catch (e) {
          config.productId = [];
        }
      }
      config.appVersion = JSON.parse(config.appVersion ?? '[]');
      config.model = JSON.parse(config.model ?? '[]');
      config.sdkVersion = JSON.parse(config.sdkVersion ?? '[]');
      config.system = JSON.parse(config.system ?? '[]');
      config.uin = JSON.parse(config.uin ?? '[]');
      config.deviceId = JSON.parse(config.deviceId ?? '[]');
      config.appVersionMode = parseAppVersionMode(config.appVersionMode ?? '[]');
      // 接口返回的after和before是json字符串，且下面app、sdk、os规则变量命名用蛇形，需要转换为小驼峰做兼容
      config.sdkVersionOperator = config.sdk_version_operator ?? MatchMode.OR;
      config.appVersionOperator = config.app_version_operator ?? MatchMode.OR;
      config.osVersionOperator = config.os_version_operator ?? MatchMode.OR;
      if (type !== ConfigTabType.COMPLIANCE && config?.platform === Platform.Android) {
        config.content = filterConfigHttp(config?.content, type);
      }
      return config;
    };

    return history?.map(item => ({
      ...item,
      defaultAfter: item.after,
      defaultBefore: item.before,
      after: formatHistory(item.after!),
      before: formatHistory(item.before!),
      key: uuid.v4(),
    }));
  } catch (e) {
    logger.error('showHistory', e);
    return null;
  }
};

const formatAppVersionMode = (appVersionMode: string[]) => appVersionMode
  ?.filter(item => item !== APP_VERSION_MODE_OPTIONS[0].value);

const parseAppVersionMode = (appVersionMode: string) => {
  const mode: string[] = JSON.parse(appVersionMode);
  if (mode.length === 0) {
    return APP_VERSION_MODE_OPTIONS[0].value;
  }
  return mode;
};
